/**
 * Role-related React Query hooks
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { identityServiceClient } from '../client'
import { RoleCreate, RolesQueryParams, RoleUpdate } from '../'

// Query hooks for roles
export const useRoles = (params?: RolesQueryParams) => {
  return useQuery({
    queryKey: ['roles', params],
    queryFn: async () => {
      const response = await identityServiceClient.GET('/roles', {
        params: { query: params },
      })
      if (response.error) throw response.error
      return response.data
    },
  })
}

export const useRole = (roleId: string) => {
  return useQuery({
    queryKey: ['role', roleId],
    queryFn: async () => {
      const response = await identityServiceClient.GET('/roles/{id}', {
        params: { path: { id: roleId } },
      })
      if (response.error) throw response.error
      return response.data
    },
    enabled: !!roleId,
  })
}

// Mutation hooks for roles
export const useCreateRole = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (roleData: RoleCreate) => {
      const response = await identityServiceClient.POST('/roles', {
        body: roleData,
      })
      if (response.error) throw response.error
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['roles'] })
    },
  })
}

export const useUpdateRole = (roleId: string) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (roleData: RoleUpdate) => {
      const response = await identityServiceClient.PUT('/roles/{id}', {
        params: { path: { id: roleId } },
        body: roleData,
      })
      if (response.error) throw response.error
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['roles'] })
      queryClient.invalidateQueries({ queryKey: ['role', roleId] })
    },
  })
}

export const useDeleteRole = (roleId: string) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async () => {
      const response = await identityServiceClient.DELETE('/roles/{id}', {
        params: { path: { id: roleId } },
      })
      if (response.error) throw response.error
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['roles'] })
      queryClient.removeQueries({ queryKey: ['role', roleId] })
    },
  })
}

export const useAssignRolePermission = (roleId: string) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (permissionData: any) => {
      const response = await identityServiceClient.POST('/roles/{id}/permissions', {
        params: { path: { id: roleId } },
        body: permissionData,
      })
      if (response.error) throw response.error
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['role-permissions', roleId] })
    },
  })
}
